import React from 'react';
import Icon from '../../../components/AppIcon';

const ViewerControls = ({ 
  isFullscreen, 
  onToggleFullscreen, 
  viewMode, 
  onViewModeChange, 
  isPlaying, 
  onToggleAnimation,
  onScreenshot,
  onShare,
  showGrid,
  onToggleGrid,
  isInfiniteVertical = false,
  onToggleInfiniteVertical,
  onMaterialColorChange,
  materialColor, 
  showLighting,
  onToggleLighting,
  isExploded,
  onToggleExplode,
  explodeScaleX,
  explodeScaleY,
  explodeScaleZ,
  onExplodeScaleXChange,
  onExplodeScaleYChange,
  onExplodeScaleZChange,
  isExplodeLocked,
  onToggleExplodeLock,
  onReset,
  isLoggedIn = false,
  onAuthRequired
}) => {
  
  const handleProtectedAction = (actionHandler) => {
    if (!isLoggedIn) {
      if (onAuthRequired) onAuthRequired();
    } else {
      if (actionHandler) actionHandler();
    }
  };

  const viewModesAndTools = [
    { id: 'shaded', label: 'Shaded', icon: 'Eye', onClick: () => onViewModeChange('shaded'), isProtected: false },
    { id: 'wireframe', label: 'Wireframe', icon: 'Grid3X3', onClick: () => onViewModeChange('wireframe'), isProtected: true },
    { 
      id: 'x-ray', 
      label: viewMode === 'x-ray' ? 'Z-Ray Color' : (viewMode === 'x-ray-gray' ? 'Z-Ray Gray' : 'Z-Ray'),
      icon: 'Scan', 
      onClick: () => {
        if (viewMode === 'x-ray') {
          onViewModeChange('x-ray-gray');
        } else if (viewMode === 'x-ray-gray') {
          onViewModeChange('shaded');
        } else {
          onViewModeChange('x-ray');
        }
      }, 
      isProtected: true,
      isSelected: viewMode === 'x-ray' || viewMode === 'x-ray-gray'
    },
    { id: 'material', label: 'Material', icon: 'Palette', onClick: () => onViewModeChange('material'), isProtected: true },
    { id: 'lighting', label: 'Lighting', icon: 'Sun', onClick: onToggleLighting, isSelected: !!showLighting, isProtected: true },
    { id: 'explode', label: 'Explode', icon: 'Asterisk', onClick: onToggleExplode, isSelected: !!isExploded, isProtected: true }
  ];

  return (
    <div className="absolute right-4 top-4 z-20 transition-all duration-300">
      <div className="flex flex-col space-y-2">
        {/* Primary Control Buttons */}
        <div className="bg-white/[0.88] backdrop-blur-md rounded-lg border border-slate-200 shadow-xl p-2">
          <div className="grid grid-cols-3 gap-1">
            {viewModesAndTools.map((control) => {
              // STRICTOR ACTIVE CALCULATION
              const active = control.isSelected !== undefined 
                ? control.isSelected 
                : (viewMode === control.id);
              
              return (
                <button
                  key={control.id}
                  type="button"
                  onClick={() => {
                    if (control.isProtected) {
                      handleProtectedAction(control.onClick);
                    } else {
                      control.onClick();
                    }
                  }}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 w-16 h-16 outline-none ${
                    active 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-white text-slate-500 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  <Icon 
                    name={control.icon} 
                    size={20} 
                    color={active ? "white" : "currentColor"}
                    className="mb-1"
                  />
                  <span className="text-[10px] font-bold uppercase tracking-tighter leading-none">{control.label}</span>
                </button>
              );
            })}
          </div>

          {viewMode === 'material' && onMaterialColorChange && (
            <div className="mt-2 p-1 bg-slate-50 rounded border border-slate-100">
              <input
                type="color"
                value={materialColor || '#ffffff'} 
                onChange={(e) => onMaterialColorChange(e.target.value)}
                className="w-full h-8 cursor-pointer rounded bg-transparent border-none"
              />
            </div>
          )}

          {isExploded && (
            <div className="mt-3 px-1 space-y-3 border-t pt-3 border-slate-100 text-left">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Explode Axes</span>
                <button
                  type="button"
                  onClick={onToggleExplodeLock}
                  className={`p-1 rounded hover:bg-slate-100 transition-colors ${
                    isExplodeLocked ? 'text-blue-600 bg-blue-50' : 'text-slate-400 bg-slate-50'
                  }`}
                  title={isExplodeLocked ? "Unlock Axes (Move Separately)" : "Lock Axes (Move Together)"}
                >
                  <Icon name={isExplodeLocked ? "Lock" : "Unlock"} size={14} color="currentColor" />
                </button>
              </div>

              {/* X Axis Slider */}
              <div className="space-y-1">
                <div className="flex justify-between text-[8px] text-slate-400 font-bold uppercase">
                  <span>X Axis</span>
                  <span className="font-mono text-[9px] text-slate-500">{Math.round(explodeScaleX * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="0.1"
                  value={explodeScaleX}
                  onChange={(e) => onExplodeScaleXChange(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-slate-200 rounded appearance-none cursor-pointer accent-blue-600 focus:outline-none"
                />
              </div>

              {/* Y Axis Slider */}
              <div className="space-y-1">
                <div className="flex justify-between text-[8px] text-slate-400 font-bold uppercase">
                  <span>Y Axis</span>
                  <span className="font-mono text-[9px] text-slate-500">{Math.round(explodeScaleY * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="0.1"
                  value={explodeScaleY}
                  onChange={(e) => onExplodeScaleYChange(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-slate-200 rounded appearance-none cursor-pointer accent-blue-600 focus:outline-none"
                />
              </div>

              {/* Z Axis Slider */}
              <div className="space-y-1">
                <div className="flex justify-between text-[8px] text-slate-400 font-bold uppercase">
                  <span>Z Axis</span>
                  <span className="font-mono text-[9px] text-slate-500">{Math.round(explodeScaleZ * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="0.1"
                  value={explodeScaleZ}
                  onChange={(e) => onExplodeScaleZChange(parseFloat(e.target.value))}
                  className="w-full h-1.5 bg-slate-200 rounded appearance-none cursor-pointer accent-blue-600 focus:outline-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Secondary Controls */}
        <div className="bg-white/[0.88] backdrop-blur-md rounded-lg border border-slate-200 shadow-xl p-2">
          <div className="flex flex-col space-y-1">
            <button
              onClick={onReset}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              <Icon name="RefreshCcw" size={18} color="currentColor" />
              <span className="text-xs font-bold uppercase tracking-tight">Reset</span>
            </button>
            
            <button
              onClick={onToggleFullscreen}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              <Icon name={isFullscreen ? "Minimize2" : "Maximize2"} size={18} color="currentColor" />
              <span className="text-xs font-bold uppercase tracking-tight">{isFullscreen ? "Exit" : "Full"}</span>
            </button>

            <button
              onClick={onToggleGrid}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                showGrid 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              <Icon name="Grid3X3" size={18} color={showGrid ? "white" : "currentColor"} />
              <span className="text-xs font-bold uppercase tracking-tight">Grid</span>
            </button>

            <button
              onClick={onToggleInfiniteVertical}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                isInfiniteVertical 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:bg-blue-50 hover:text-blue-600'
              }`}
              title={isInfiniteVertical ? "Lock vertical orbit limits" : "Enable infinite spherical orbit tumbling"}
            >
              <Icon name="ArrowUpDown" size={18} color={isInfiniteVertical ? "white" : "currentColor"} />
              <span className="text-xs font-bold uppercase tracking-tight">Infinite</span>
            </button>

            <button
              onClick={() => handleProtectedAction(onToggleAnimation)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                isPlaying 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-slate-500 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              <Icon name={isPlaying ? "Pause" : "Play"} size={18} color={isPlaying ? "white" : "currentColor"} />
              <span className="text-xs font-bold uppercase tracking-tight">Anim</span>
            </button>

            <button
              onClick={() => handleProtectedAction(onScreenshot)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              <Icon name="Camera" size={18} color="currentColor" />
              <span className="text-xs font-bold uppercase tracking-tight">Snap</span>
            </button>

            <button
              onClick={onShare}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
            >
              <Icon name="Share2" size={18} color="currentColor" />
              <span className="text-xs font-bold uppercase tracking-tight">Share</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewerControls;
